import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Container } from "reactstrap";
import Paginate from "../../components/shared/paginate";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { FetchPayoutSummary, FetchHostBalances } from "../../apis/Payout";
import { formatCurrency } from "./payoutHelpers";

const PayoutDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [hosts, setHosts] = useState([]);
  const [currentPage, setCurrentPage] = useState("1");
  const [totalPages, setTotalPages] = useState("1");
  const [loading, setLoading] = useState(true);
  const limit = "10";

  const fetchSummary = async () => {
    try {
      const res = await FetchPayoutSummary();
      if (res.success) {
        setSummary(res.data?.summary);
      }
    } catch (error) {
      toast.error("Failed to fetch payout summary.");
    }
  };

  const fetchHosts = async () => {
    try {
      setLoading(true);
      const res = await FetchHostBalances(currentPage, limit);
      if (res.success) {
        setHosts(res.data?.hostBalances || []);
        setTotalPages(res.data?.totalPages?.toString() || "1");
      }
    } catch (error) {
      toast.error("Failed to fetch host balances.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, []);

  useEffect(() => {
    fetchHosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const columns = useMemo(
    () => [
      "Host",
      "Email",
      "Reservations",
      "Total Earned",
      "Host Earnings",
      "Total Paid",
      "Available Balance",
      "Actions",
    ],
    [],
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container className="px-0 dark:bg-darkBg1 dark:text-[#c7d8eb]" fluid>
          <div className="p-6 flex flex-col gap-6">
            <h1 className="text-2xl dark:text-[#c7d8eb]">Payout Dashboard</h1>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-[#ffffff] dark:bg-darkBg2 border border-gray-300 dark:border-gray-600 rounded-lg p-4">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Pending Payouts
                </div>
                <div className="text-2xl font-bold mt-1">
                  {summary?.pendingPayouts ?? "-"}
                </div>
              </div>
              <div className="bg-[#ffffff] dark:bg-darkBg2 border border-gray-300 dark:border-gray-600 rounded-lg p-4">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Total Owed to Hosts
                </div>
                <div className="text-2xl font-bold mt-1 text-red-500">
                  {summary ? formatCurrency(summary.totalOwedToHosts) : "-"}
                </div>
              </div>
              <div className="bg-[#ffffff] dark:bg-darkBg2 border border-gray-300 dark:border-gray-600 rounded-lg p-4">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Paid This Month
                </div>
                <div className="text-2xl font-bold mt-1 text-green-500">
                  {summary ? formatCurrency(summary.paidThisMonth) : "-"}
                </div>
              </div>
              <div className="bg-[#ffffff] dark:bg-darkBg2 border border-gray-300 dark:border-gray-600 rounded-lg p-4">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Total Paid Out
                </div>
                <div className="text-2xl font-bold mt-1">
                  {summary ? formatCurrency(summary.totalPaidOut) : "-"}
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex gap-4">
              <Link
                to="/payouts/history"
                className="text-secondaryColor hover:underline text-sm font-semibold"
              >
                View Payout History →
              </Link>
            </div>

            {/* Host Balances Table */}
            <div className="lg:flex flex-col lg:border lg:border-b-0 border-gray-400 dark:border-white">
              <div className="border-x border-t lg:border-none border-gray-500 dark:border-white rounded-[6px_6px_0px_0px] w-full flex flex-col lg:flex-row justify-between gap-4 p-4">
                <h2 className="text-lg font-semibold">Host Balances</h2>
              </div>
              <div className="overflow-x-auto w-full">
                {/* Desktop Table */}
                <div className="hidden lg:flex">
                  {columns.map((column, index) => (
                    <div
                      key={`column-${index}`}
                      className={`${
                        index === 7 ? "w-[10%]" : "w-[13%]"
                      } min-w-[120px] content-center border-y border-gray-500 dark:border-white break-words py-2 text-center font-semibold text-xs`}
                    >
                      {column}
                    </div>
                  ))}
                </div>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
                  </div>
                ) : hosts.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No host balances found.
                  </div>
                ) : (
                  hosts.map((item, index) => (
                    <React.Fragment key={`host-${index}`}>
                      {/* Desktop Row */}
                      <div className="hidden lg:flex w-full">
                        <div className="w-[13%] min-w-[120px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center text-xs">
                          {item.host?.firstName} {item.host?.lastName}
                        </div>
                        <div className="w-[13%] min-w-[120px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center text-xs">
                          {item.host?.email}
                        </div>
                        <div className="w-[13%] min-w-[120px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center text-xs">
                          {item.totalReservations}
                        </div>
                        <div className="w-[13%] min-w-[120px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center text-xs">
                          {formatCurrency(item.totalEarned)}
                        </div>
                        <div className="w-[13%] min-w-[120px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center text-xs">
                          {formatCurrency(item.hostEarnings)}
                        </div>
                        <div className="w-[13%] min-w-[120px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center text-xs">
                          {formatCurrency(item.totalPaidOut)}
                        </div>
                        <div className="w-[13%] min-w-[120px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center text-xs font-semibold">
                          <span
                            className={
                              item.availableBalance > 0
                                ? "text-green-600"
                                : "text-gray-500"
                            }
                          >
                            {formatCurrency(item.availableBalance)}
                          </span>
                        </div>
                        <div className="w-[10%] min-w-[120px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          <Link
                            to={`/payouts/host/${item.host?._id}`}
                            className="bg-secondaryColor text-white px-3 py-1 rounded text-xs hover:opacity-80"
                          >
                            Pay
                          </Link>
                        </div>
                      </div>

                      {/* Mobile Card */}
                      <div className="block lg:hidden mb-6 border-x border-y border-gray-500 dark:border-white">
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Host</div>
                          <div>
                            {item.host?.firstName} {item.host?.lastName}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Email</div>
                          <div className="text-sm">{item.host?.email}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Reservations</div>
                          <div>{item.totalReservations}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Total Earned</div>
                          <div>{formatCurrency(item.totalEarned)}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Host Earnings</div>
                          <div>{formatCurrency(item.hostEarnings)}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Total Paid</div>
                          <div>{formatCurrency(item.totalPaidOut)}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Available Balance</div>
                          <div
                            className={`font-semibold ${
                              item.availableBalance > 0
                                ? "text-green-600"
                                : "text-gray-500"
                            }`}
                          >
                            {formatCurrency(item.availableBalance)}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Actions</div>
                          <Link
                            to={`/payouts/host/${item.host?._id}`}
                            className="bg-secondaryColor text-white px-3 py-1 rounded text-xs hover:opacity-80"
                          >
                            Pay
                          </Link>
                        </div>
                      </div>
                    </React.Fragment>
                  ))
                )}
              </div>
            </div>
            <Paginate
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
            />
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PayoutDashboard;
